import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { GapResponseSection } from '../GapResponseSection';
import { Patient360TabPanel } from './Patient360TabPanel';
import {
  type AnsweredGapQuestionRecord,
  type GapDomainColors,
  type Patient360Details,
  type Patient360ResolutionHandler,
} from './types';

interface Patient360CoreTabsProps {
  value: number;
  patientDetails: Patient360Details;
  demographicGapResponses: AnsweredGapQuestionRecord[];
  clinicalGapResponses: AnsweredGapQuestionRecord[];
  domainColors: GapDomainColors;
  onResolutionChange: Patient360ResolutionHandler;
  getLabStatusColor: (status: string) => 'success' | 'warning' | 'error' | 'default';
}

export const Patient360CoreTabs = ({
  value,
  patientDetails,
  demographicGapResponses,
  clinicalGapResponses,
  domainColors,
  onResolutionChange,
  getLabStatusColor,
}: Patient360CoreTabsProps) => (
  <>
    <Patient360TabPanel value={value} index={0}>
      <Stack spacing={2}>
        <Alert severity="info" icon={<InfoIcon />}>
          Data Source: {patientDetails.demographics.source} • Last Updated: {patientDetails.demographics.lastUpdated}
        </Alert>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><Card><CardContent><Typography variant="subtitle2" color="text.secondary">Full Name</Typography><Typography variant="h6">{patientDetails.demographics.data.firstName} {patientDetails.demographics.data.lastName}</Typography></CardContent></Card></Grid>
          <Grid item xs={12} sm={6}><Card><CardContent><Typography variant="subtitle2" color="text.secondary">Medical Record Number</Typography><Typography variant="h6">{patientDetails.demographics.data.mrn}</Typography></CardContent></Card></Grid>
          <Grid item xs={12} sm={6}><Card><CardContent><Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography><Typography variant="body1">{patientDetails.demographics.data.dateOfBirth} (Age {patientDetails.demographics.data.age})</Typography></CardContent></Card></Grid>
          <Grid item xs={12} sm={6}><Card><CardContent><Typography variant="subtitle2" color="text.secondary">Contact</Typography><Typography variant="body2">{patientDetails.demographics.data.email}</Typography><Typography variant="body2">{patientDetails.demographics.data.phone}</Typography></CardContent></Card></Grid>
          <Grid item xs={12}><Card><CardContent><Typography variant="subtitle2" color="text.secondary" gutterBottom>Emergency Contact</Typography><Typography variant="body1">{patientDetails.demographics.data.emergencyContact.name} ({patientDetails.demographics.data.emergencyContact.relationship})</Typography><Typography variant="body2" color="text.secondary">{patientDetails.demographics.data.emergencyContact.phone}</Typography></CardContent></Card></Grid>
        </Grid>

        <GapResponseSection
          title="Family Questions & Responses"
          items={demographicGapResponses}
          emptyText="No answered family-history or household-context questions yet."
          domainColors={domainColors}
          onResolutionChange={onResolutionChange}
        />
      </Stack>
    </Patient360TabPanel>

    <Patient360TabPanel value={value} index={1}>
      <Stack spacing={3}>
        <Alert severity="info" icon={<InfoIcon />}>
          Data Source: {patientDetails.clinicalInfo.source} • Last Updated: {patientDetails.clinicalInfo.lastUpdated}
        </Alert>

        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Primary Diagnosis</Typography>
            <Typography variant="body1">{patientDetails.clinicalInfo.data.primaryDiagnosis} ({patientDetails.clinicalInfo.data.icdCode})</Typography>
            <Typography variant="caption" color="text.secondary">Diagnosis Date: {patientDetails.clinicalInfo.data.diagnosisDate}</Typography>
          </CardContent>
        </Card>

        <Box>
          <Typography variant="h6" gutterBottom>Active Conditions</Typography>
          <TableContainer component={Paper}><Table><TableHead><TableRow><TableCell>Condition</TableCell><TableCell>ICD Code</TableCell><TableCell>Onset Date</TableCell><TableCell>Status</TableCell></TableRow></TableHead><TableBody>{patientDetails.clinicalInfo.data.conditions.map((condition, idx) => (<TableRow key={idx}><TableCell>{condition.name}</TableCell><TableCell>{condition.icdCode}</TableCell><TableCell>{condition.onsetDate}</TableCell><TableCell><Chip label={condition.status} color="success" size="small" /></TableCell></TableRow>))}</TableBody></Table></TableContainer>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom>Allergies</Typography>
          <Stack direction="row" spacing={1}>{patientDetails.clinicalInfo.data.allergies.map((allergy, idx) => (<Chip key={idx} label={`${allergy.allergen} - ${allergy.reaction} (${allergy.severity})`} color="error" icon={<WarningIcon />} />))}</Stack>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom>Recent Vitals</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}><Card><CardContent><Typography variant="caption" color="text.secondary">Height</Typography><Typography variant="h6">{patientDetails.clinicalInfo.data.vitals.height}</Typography></CardContent></Card></Grid>
            <Grid item xs={6} sm={3}><Card><CardContent><Typography variant="caption" color="text.secondary">Weight</Typography><Typography variant="h6">{patientDetails.clinicalInfo.data.vitals.weight}</Typography></CardContent></Card></Grid>
            <Grid item xs={6} sm={3}><Card><CardContent><Typography variant="caption" color="text.secondary">BMI</Typography><Typography variant="h6">{patientDetails.clinicalInfo.data.vitals.bmi}</Typography></CardContent></Card></Grid>
            <Grid item xs={6} sm={3}><Card><CardContent><Typography variant="caption" color="text.secondary">Blood Pressure</Typography><Typography variant="h6">{patientDetails.clinicalInfo.data.vitals.bloodPressure}</Typography></CardContent></Card></Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>Recorded: {patientDetails.clinicalInfo.data.vitals.recordedDate}</Typography>
        </Box>

        <Box>
          <Typography variant="h6" gutterBottom>Current Medications</Typography>
          <TableContainer component={Paper}><Table><TableHead><TableRow><TableCell>Medication</TableCell><TableCell>Dosage</TableCell><TableCell>Frequency</TableCell><TableCell>Prescriber</TableCell><TableCell>Status</TableCell></TableRow></TableHead><TableBody>{patientDetails.medications.data.map((med, idx) => (<TableRow key={idx}><TableCell>{med.name}</TableCell><TableCell>{med.dosage}</TableCell><TableCell>{med.frequency}</TableCell><TableCell>{med.prescriber}</TableCell><TableCell><Chip label={med.status} color="success" size="small" /></TableCell></TableRow>))}</TableBody></Table></TableContainer>
        </Box>

        <GapResponseSection title="Clinical Questions & Responses" items={clinicalGapResponses} emptyText="No answered clinical gap questions yet." domainColors={domainColors} onResolutionChange={onResolutionChange} />
      </Stack>
    </Patient360TabPanel>

    <Patient360TabPanel value={value} index={2}>
      <Stack spacing={3}>
        <Alert severity="info" icon={<InfoIcon />}>
          Data Source: {patientDetails.labResults.source} • Last Updated: {patientDetails.labResults.lastUpdated}
        </Alert>

        {patientDetails.labResults.gaps.length > 0 && (
          <Alert severity="warning" icon={<WarningIcon />}>
            <Typography variant="subtitle2" gutterBottom>Data Gaps Identified</Typography>
            {patientDetails.labResults.gaps.map((gap, idx) => (
              <Typography key={idx} variant="body2">• {gap.missingTest}: {gap.recommendation} (Last checked: {gap.lastChecked})</Typography>
            ))}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead><TableRow><TableCell>Test Name</TableCell><TableCell>Value</TableCell><TableCell>Reference Range</TableCell><TableCell>Status</TableCell><TableCell>Date</TableCell><TableCell>Ordered By</TableCell></TableRow></TableHead>
            <TableBody>
              {patientDetails.labResults.data.map((lab, idx) => (
                <TableRow key={idx}>
                  <TableCell>{lab.testName}</TableCell>
                  <TableCell><strong>{lab.value}</strong> {lab.unit}</TableCell>
                  <TableCell>{lab.referenceRange}</TableCell>
                  <TableCell>
                    <Chip label={lab.status.replace('_', ' ')} color={getLabStatusColor(lab.status)} size="small" icon={lab.status === 'NORMAL' ? <CheckCircleIcon /> : <WarningIcon />} />
                  </TableCell>
                  <TableCell>{lab.date}</TableCell>
                  <TableCell>{lab.orderedBy}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Patient360TabPanel>
  </>
);